/**
 * The schedule module-toggle system: one descriptor table drives the flag
 * registry, the Features page switch rows, and default resolution. Pure - no Vue,
 * no Firestore. Consumed by SchedulePage's `scheduleFeatures` computed and by the
 * Scheduling card in staff/Features.vue.
 *
 * Mirrors formfactories-vue3/src/features/schedule/lib/scheduleFeatureFlags.ts;
 * both apps read the same `enterprises/{id}/config/public` keys, so the ids, the
 * flag keys and the grandfather list must stay identical in both files.
 */

import { SCHEDULE_ENABLED_ENTERPRISE_IDS } from '../../../config'

/**
 * Enterprises grandfathered to the FULL module set: an ABSENT flag resolves ON
 * for them, OFF for everyone else ("simple by default"), so the live
 * enterprises keep every module with ZERO config writes.
 *
 * Deliberately a SEPARATE constant from config.js's
 * SCHEDULE_ENABLED_ENTERPRISE_IDS (the page-access default) even though the
 * values coincide today: turning the scheduler on for a new enterprise must not
 * silently grandfather it into every module.
 * Ids: TgNi8... = Preview (dev sandbox / prod testing), t9WXK... = Incept3D.
 */
export const SCHEDULE_LEGACY_FULL_ENTERPRISE_IDS = [
  'TgNi8mpKJTnZ3Lt4Dzfp',
  't9WXKqgdGpfQdxN54DKi',
]

/**
 * One row per module; the single source of truth for flags + settings rows.
 * `grandfatherList` is per-row so a module added after the grandfather era can
 * ship `[]` (nobody grandfathered) rather than copying an allowlist.
 */
export const SCHEDULE_FEATURE_FLAGS = [
  {
    id: 'machines',
    flagKey: 'enableScheduleMachines',
    label: 'Machine assignments',
    description: 'Tag shifts and staff with the machines they run.',
    grandfatherList: SCHEDULE_LEGACY_FULL_ENTERPRISE_IDS,
  },
  {
    id: 'projects',
    flagKey: 'enableScheduleProjects',
    label: 'Project assignments',
    description: 'Tag shifts and staff with projects.',
    grandfatherList: SCHEDULE_LEGACY_FULL_ENTERPRISE_IDS,
  },
  {
    id: 'recurring',
    flagKey: 'enableScheduleRecurring',
    label: 'Repeating shifts',
    description: 'Weekly repeating shift series with per-series colors.',
    grandfatherList: SCHEDULE_LEGACY_FULL_ENTERPRISE_IDS,
  },
  {
    id: 'multiPerson',
    flagKey: 'enableScheduleMultiPerson',
    // Seat vocabulary only. Choosing WHO may fill a shift deliberately left this
    // module, so a one-person shop can still reach "Locked".
    label: 'Multi-person shifts',
    description: 'Seat counts, open shifts, and letting staff claim an open seat.',
    grandfatherList: SCHEDULE_LEGACY_FULL_ENTERPRISE_IDS,
  },
  {
    id: 'locations',
    flagKey: 'enableScheduleLocations',
    label: 'Location filters',
    description: 'Filter the schedule and its shifts by location.',
    grandfatherList: SCHEDULE_LEGACY_FULL_ENTERPRISE_IDS,
  },
  {
    id: 'roles',
    flagKey: 'enableScheduleRoles',
    label: 'Role assignments',
    description: 'Tag assigned staff with project roles.',
    grandfatherList: SCHEDULE_LEGACY_FULL_ENTERPRISE_IDS,
  },
]

const byId = SCHEDULE_FEATURE_FLAGS.reduce((map, row) => {
  map[row.id] = row
  return map
}, {})

/** The descriptor row for `id`; throws on an unknown id (a wiring bug, not data). */
export function scheduleFeatureFlag(id) {
  const row = byId[id]
  if (!row) throw new Error(`Unknown schedule feature id: ${id}`)
  return row
}

/**
 * What an ABSENT flag resolves to for this enterprise - the grandfather default.
 * Exported separately because the Features card MUST seed each switch with it
 * (a literal `false` would show a legacy enterprise's switch OFF while the
 * scheduler renders the module ON, and saving would write destructive `false`s).
 */
export function scheduleGrandfatherDefault(id, enterpriseId) {
  return scheduleFeatureFlag(id).grandfatherList.includes(enterpriseId)
}

/**
 * Resolves a module toggle: explicit `true`/`false` on `config/public` wins; an
 * absent key falls back to the grandfather default. Tolerates a null/still-
 * loading config (treated as all-absent), so a denied or pre-auth config read
 * degrades to the grandfather default instead of throwing.
 */
export function resolveScheduleFlag(id, enterpriseId, configPublic) {
  const stored = configPublic ? configPublic[scheduleFeatureFlag(id).flagKey] : undefined
  if (stored === true) return true
  if (stored === false) return false
  return scheduleGrandfatherDefault(id, enterpriseId)
}

/**
 * The scheduler page itself: a Beta switch on `config/public`, separate from
 * the module toggles above. An explicit `true`/`false` wins. An absent key
 * falls back to config.js's SCHEDULE_ENABLED_ENTERPRISE_IDS, so the
 * enterprises that already had the scheduler keep it with zero config writes
 * and every other enterprise starts OFF.
 * Vue 3 analog: formfactories-vue3/src/features/schedule/config.ts.
 */
export const SCHEDULE_PAGE_FLAG_KEY = 'enableSchedule'

/** What an ABSENT `enableSchedule` resolves to for this enterprise. */
export function scheduleEnabledDefault(enterpriseId) {
  return !!enterpriseId && SCHEDULE_ENABLED_ENTERPRISE_IDS.includes(enterpriseId)
}

/** Whether the scheduler is on for this enterprise. Null config reads as all-absent. */
export function resolveScheduleEnabled(enterpriseId, configPublic) {
  if (!enterpriseId) return false
  const stored = configPublic ? configPublic[SCHEDULE_PAGE_FLAG_KEY] : undefined
  if (stored === true) return true
  if (stored === false) return false
  return scheduleEnabledDefault(enterpriseId)
}
